import { type FC, type HTMLAttributes, memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { prefixes, useTypedTranslation } from "@/locales";
import type { Placement } from "@react-types/overlays";
import { Brush02, CheckCircle, Edit05, LogOut01, MessageQuestionCircle, User01 } from "@untitledui/icons";
import { useFocusManager } from "react-aria";
import {
    Button as AriaButton,
    Dialog as AriaDialog,
    type DialogProps as AriaDialogProps,
    DialogTrigger as AriaDialogTrigger,
    OverlayTriggerStateContext,
    Popover as AriaPopover
} from "react-aria-components";
import { useBreakpoint } from "@/hooks/use-breakpoint.ts";
import { cx } from "@/utils/cx.ts";
import { Avatar } from "@/components/_untitled/base/avatar/avatar";
import { getAvatarColor, getInitials } from "@/utils/avatar";

export const PublicNavAccountMenu = ({
                                         className,
                                         onSettingsClick,
                                         onAdminModeClick,
                                         onKycClick,
                                         kycProgress,
                                         onSignOut,
                                         ...dialogProps
                                     }: AriaDialogProps & {
    className?: string;
    onSettingsClick: () => void;
    onAdminModeClick?: () => void;
    onKycClick?: () => void;
    kycProgress?: { completed: number; total: number };
    onSignOut?: () => void;
}) => {
    const [tAccountMenu] = useTypedTranslation(prefixes('header.accountMenu'));
    const focusManager = useFocusManager();
    const dialogRef = useRef<HTMLDivElement>(null);
    const state = useContext(OverlayTriggerStateContext);

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    focusManager?.focusNext({ tabbable: true, wrap: true });
                    break;
                case "ArrowUp":
                    focusManager?.focusPrevious({ tabbable: true, wrap: true });
                    break;
            }
        },
        [focusManager],
    );

    useEffect(() => {
        const element = dialogRef.current;
        if (element) {
            element.addEventListener("keydown", onKeyDown);
        }

        return () => {
            if (element) {
                element.removeEventListener("keydown", onKeyDown);
            }
        };
    }, [onKeyDown]);

    return (
        <AriaDialog
            { ...dialogProps }
            ref={ dialogRef }
            aria-label={ tAccountMenu('menu') }
            className={ cx("w-66 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden", className) }
        >
            <nav className="rounded-xl bg-primary ring-1 ring-secondary">
                <div className="flex flex-col gap-0.5 py-1.5">
                    <PublicNavAccountMenuItem
                        label={ tAccountMenu('viewProfile') }
                        icon={ User01 }
                        onClick={ () => {
                            onSettingsClick();
                            state?.close();
                        } }
                    />
                    { onAdminModeClick && (
                        <PublicNavAccountMenuItem
                            label={ tAccountMenu('switchToAdmin') }
                            icon={ Edit05 }
                            onClick={ onAdminModeClick }
                            aria-label={ tAccountMenu('switchToAdmin') }
                        />
                    ) }
                    { kycProgress && onKycClick && (
                        kycProgress.completed < kycProgress.total ? (
                            <PublicNavAccountMenuItem
                                label={ tAccountMenu('completeKyc', {
                                    completed: kycProgress.completed,
                                    total: kycProgress.total
                                }) }
                                icon={ CheckCircle }
                                onClick={ onKycClick }
                                aria-label={ tAccountMenu('completeKyc', {
                                    completed: kycProgress.completed,
                                    total: kycProgress.total
                                }) }
                            />
                        ) : (
                            <PublicNavAccountMenuItem
                                label={ tAccountMenu('showBidderNumber') }
                                icon={ Brush02 }
                                onClick={ onKycClick }
                                aria-label={ tAccountMenu('showBidderNumber') + ' #' }
                            />
                        )
                    ) }
                    <PublicNavAccountMenuItem
                        label={ tAccountMenu('support') }
                        icon={ MessageQuestionCircle }
                        isDisabled
                        aria-label={ tAccountMenu('support') }
                    />
                </div>
            </nav>

            <div className="pt-1 pb-1.5">
                <PublicNavAccountMenuItem
                    data-testid="logout-button"
                    label={ tAccountMenu('signOut') }
                    icon={ LogOut01 }
                    onClick={ onSignOut }
                    aria-label={ tAccountMenu('signOut') }
                />
            </div>
        </AriaDialog>
    );
};

const PublicNavAccountMenuItem = memo(({
                                           icon: Icon,
                                           label,
                                           badgeContent,
                                           isDisabled,
                                           ...buttonProps
                                       }: {
    icon?: FC<{ className?: string }>;
    label: string;
    badgeContent?: string | number;
    isDisabled?: boolean;
} & HTMLAttributes<HTMLButtonElement>) => {
    return (
        <button
            { ...buttonProps }
            disabled={ isDisabled }
            className={ cx(
                "group/item flex w-full items-center gap-3 rounded-md p-2 ps-3.5 pe-3.5 text-sm font-semibold text-secondary outline-focus-ring transition-colors focus-visible:outline-2 focus-visible:outline-offset-2",
                isDisabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:bg-primary_hover hover:text-secondary_hover",
                buttonProps.className
            ) }
        >
            { Icon && <Icon className="size-5 text-fg-quaternary"/> }
            <span className="flex-1 text-start">{ label }</span>
            { badgeContent !== undefined && (
                <span className="text-xs font-semibold text-fg-quaternary">
                    #{ badgeContent }
                </span>
            ) }
        </button>
    );
});

PublicNavAccountMenuItem.displayName = "PublicNavAccountMenuItem";

export const PublicNavAccountCard = ({
                                         popoverPlacement,
                                         user,
                                         onSettingsClick,
                                         onAdminModeClick,
                                         onKycClick,
                                         kycProgress,
                                         onSignOut,
                                     }: {
    popoverPlacement?: Placement;
    user?: { name: string; email: string; avatar: string };
    onSettingsClick: () => void;
    onAdminModeClick?: () => void;
    onKycClick?: () => void;
    kycProgress?: { completed: number; total: number };
    onSignOut?: () => void;
}) => {
    const [tHeader, tAccountMenu] = useTypedTranslation(prefixes('header', 'header.accountMenu'));
    const triggerRef = useRef<HTMLDivElement>(null);
    const isDesktop = useBreakpoint("lg");
    const [isOpen, setIsOpen] = useState(false);

    const handleKycClick = useCallback(() => {
        onKycClick?.();
        setIsOpen(false);
    }, [onKycClick]);

    if (!user) {
        return null;
    }

    const firstName = user.name.split(' ')[0];
    const initials = getInitials(user.name);
    const avatarColor = getAvatarColor(user.email);

    return (
        <div ref={ triggerRef } className="relative flex items-center gap-3">
            <AriaDialogTrigger isOpen={ isOpen } onOpenChange={ setIsOpen }>
                <AriaButton
                    data-testid="user-profile-button"
                    aria-label={ tAccountMenu('menu') }
                    className="flex cursor-pointer items-center gap-3 outline-focus-ring transition hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    <span className="text-sm font-medium text-gray-700 hidden sm:block">
                        { tHeader('greeting', { name: firstName }) }
                    </span>
                    <Avatar
                        size="md"
                        src={ user.avatar || null }
                        alt={ user.name }
                        initials={ initials }
                        className={ cx(!user.avatar && avatarColor) }
                        initialsClassName="text-white"
                        contrastBorder={ false }
                    />
                </AriaButton>
                <AriaPopover
                    placement={ popoverPlacement ?? (isDesktop ? "bottom end" : "top end") }
                    triggerRef={ triggerRef }
                    offset={ 8 }
                    className={ ({ isEntering, isExiting }) =>
                        cx(
                            "will-change-transform",
                            isEntering &&
                            "duration-150 ease-out animate-in fade-in placement-bottom:origin-top placement-bottom:slide-in-from-top-0.5 placement-top:origin-bottom placement-top:slide-in-from-bottom-0.5",
                            isExiting &&
                            "duration-100 ease-in animate-out fade-out placement-bottom:origin-top placement-bottom:slide-out-to-top-0.5 placement-top:origin-bottom placement-top:slide-out-to-bottom-0.5",
                        )
                    }
                >
                    <PublicNavAccountMenu
                        onSettingsClick={ onSettingsClick }
                        onAdminModeClick={ onAdminModeClick }
                        onKycClick={ handleKycClick }
                        kycProgress={ kycProgress }
                        onSignOut={ onSignOut }
                    />
                </AriaPopover>
            </AriaDialogTrigger>
        </div>
    );
};
